import { useEffect, useMemo, useState } from "react";

import {
  AlertTriangle,
  Droplet,
  History,
  MoreHorizontal,
  Package,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Trash2,
} from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Input } from "~/components/ui/input";
import { Skeleton } from "~/components/ui/skeleton";
import {
  SortableTableHead,
  sortItems,
  useTableSort,
} from "~/components/ui/sortable-table-head";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import {
  getStockAt,
  useDeleteOilType,
  useDeleteRawMaterial,
  useLowStockOilTypes,
  useLowStockRawMaterials,
  useOilTypeStockByPlant,
  useOilTypes,
  useRawMaterialStockByPlant,
  useRawMaterials,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import type { OilType, RawMaterial } from "~/types/purchasing";
import type { IngredientType } from "~/types/purchasing";
import { costPerKgClp } from "~/utils/oilConversions";

import { NewIngredientDialog } from "./components/NewIngredientDialog";
import { OilTypeDialog } from "./components/OilTypeDialog";
import { RawMaterialDialog } from "./components/RawMaterialDialog";
import { StockAdjustmentDialog } from "./components/StockAdjustmentDialog";
import { StockMovementsDialog } from "./components/StockMovementsDialog";

export function IngredientStockPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("oil");

  // Sort state (one per item type; applies to both search and tabbed views)
  const { sort: oilSort, toggleSort: handleOilSort } = useTableSort();
  const { sort: materialSort, toggleSort: handleMaterialSort } = useTableSort();

  // Search state with debounce
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showOilDialog, setShowOilDialog] = useState(false);
  const [showMaterialDialog, setShowMaterialDialog] = useState(false);
  const [editingOil, setEditingOil] = useState<OilType | null>(null);
  const [editingMaterial, setEditingMaterial] = useState<RawMaterial | null>(
    null
  );

  // Delete confirmation states
  const [deletingOil, setDeletingOil] = useState<OilType | null>(null);
  const [deletingMaterial, setDeletingMaterial] = useState<RawMaterial | null>(
    null
  );

  // Movement and adjustment dialog states
  const [movementsIngredient, setMovementsIngredient] = useState<{
    type: IngredientType;
    ingredient: OilType | RawMaterial;
  } | null>(null);
  const [adjustmentIngredient, setAdjustmentIngredient] = useState<{
    type: IngredientType;
    ingredient: OilType | RawMaterial;
  } | null>(null);

  // Queries
  const { data: oilTypes = [], isLoading: isLoadingOil } = useOilTypes();
  const { data: lowStockOil = [] } = useLowStockOilTypes();
  const { data: rawMaterials = [], isLoading: isLoadingMaterials } =
    useRawMaterials();
  const { data: lowStockMaterials = [] } = useLowStockRawMaterials();
  const { data: oilStockByPlant } = useOilTypeStockByPlant();
  const { data: rawMaterialStockByPlant } = useRawMaterialStockByPlant();

  // Filter oil types and raw materials based on search term
  const filteredOilTypes = useMemo(() => {
    if (!searchTerm.trim()) return oilTypes;
    const term = searchTerm.toLowerCase().trim();
    return oilTypes.filter(
      (oil) =>
        oil.code.toLowerCase().includes(term) ||
        oil.name.toLowerCase().includes(term)
    );
  }, [oilTypes, searchTerm]);

  const filteredRawMaterials = useMemo(() => {
    // First filter out materials excluded from inventory
    let materials = rawMaterials.filter((m) => !m.exclude_from_inventory);

    // Then apply search filter
    if (!searchTerm.trim()) return materials;
    const term = searchTerm.toLowerCase().trim();
    return materials.filter(
      (material) =>
        material.code.toLowerCase().includes(term) ||
        material.name.toLowerCase().includes(term)
    );
  }, [rawMaterials, searchTerm]);

  // Mutations
  const deleteOilMutation = useDeleteOilType();
  const deleteMaterialMutation = useDeleteRawMaterial();

  const lowStockOilIds = new Set(lowStockOil.map((o) => o.id));
  // Filter out excluded materials from low stock alerts
  const activeLowStockMaterials = lowStockMaterials.filter(
    (m) => !m.exclude_from_inventory
  );
  const lowStockMaterialIds = new Set(activeLowStockMaterials.map((m) => m.id));

  const totalLowStock = lowStockOil.length + activeLowStockMaterials.length;

  const formatNumber = (value: number, decimals = 2) => {
    return new Intl.NumberFormat("es-CL", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value);
  };

  // Per-plant tables are the source of truth for stock; the Total column sums
  // P1 + P2 directly so it always matches what the user sees in the plant columns.
  const oilTotalStock = (oilId: string) =>
    getStockAt(oilStockByPlant, oilId, "P1") +
    getStockAt(oilStockByPlant, oilId, "P2");
  const materialTotalStock = (materialId: string) =>
    getStockAt(rawMaterialStockByPlant, materialId, "P1") +
    getStockAt(rawMaterialStockByPlant, materialId, "P2");

  // No Conforme (quarantine) stock is shown in its own column and deliberately
  // excluded from the Total above — it is not available for production.
  const oilNcStock = (oilId: string) => getStockAt(oilStockByPlant, oilId, "NC");
  const materialNcStock = (materialId: string) =>
    getStockAt(rawMaterialStockByPlant, materialId, "NC");

  // Rank for the "Estado" column so sorting surfaces problems first:
  // negative stock (0) < low stock (1) < ok (2).
  const oilStatusRank = (oilId: string) => {
    if (
      getStockAt(oilStockByPlant, oilId, "P1") < 0 ||
      getStockAt(oilStockByPlant, oilId, "P2") < 0
    )
      return 0;
    return lowStockOilIds.has(oilId) ? 1 : 2;
  };
  const materialStatusRank = (materialId: string) => {
    if (
      getStockAt(rawMaterialStockByPlant, materialId, "P1") < 0 ||
      getStockAt(rawMaterialStockByPlant, materialId, "P2") < 0
    )
      return 0;
    return lowStockMaterialIds.has(materialId) ? 1 : 2;
  };

  const getOilSortValue = (
    oil: OilType,
    field: string
  ): string | number | null => {
    switch (field) {
      case "code":
        return oil.code;
      case "name":
        return oil.name;
      case "p1":
        return getStockAt(oilStockByPlant, oil.id, "P1");
      case "p2":
        return getStockAt(oilStockByPlant, oil.id, "P2");
      case "total":
        return oilTotalStock(oil.id);
      case "nc":
        return oilNcStock(oil.id);
      case "minimum":
        return oil.minimum_stock_liters ?? null;
      case "density":
        return oil.density_kg_per_liter;
      case "cost":
        // Sorted by the per-KG cost shown in the table (density varies per oil).
        return oil.average_cost_clp != null
          ? oil.average_cost_clp / oil.density_kg_per_liter
          : null;
      case "status":
        return oilStatusRank(oil.id);
      default:
        return null;
    }
  };

  const getMaterialSortValue = (
    material: RawMaterial,
    field: string
  ): string | number | null => {
    switch (field) {
      case "code":
        return material.code;
      case "name":
        return material.name;
      case "unit":
        return material.unit;
      case "p1":
        return getStockAt(rawMaterialStockByPlant, material.id, "P1");
      case "p2":
        return getStockAt(rawMaterialStockByPlant, material.id, "P2");
      case "total":
        return materialTotalStock(material.id);
      case "nc":
        return materialNcStock(material.id);
      case "minimum":
        return material.minimum_stock_level ?? null;
      case "cost":
        return material.average_cost_clp ?? null;
      case "status":
        return materialStatusRank(material.id);
      default:
        return null;
    }
  };

  const sortedFilteredOilTypes = sortItems(
    filteredOilTypes,
    oilSort,
    getOilSortValue
  );
  const sortedOilTypes = sortItems(oilTypes, oilSort, getOilSortValue);
  const sortedFilteredRawMaterials = sortItems(
    filteredRawMaterials,
    materialSort,
    getMaterialSortValue
  );
  const sortedRawMaterials = sortItems(
    rawMaterials,
    materialSort,
    getMaterialSortValue
  );

  const handleEditOil = (oil: OilType) => {
    setEditingOil(oil);
    setShowOilDialog(true);
  };

  const handleEditMaterial = (material: RawMaterial) => {
    setEditingMaterial(material);
    setShowMaterialDialog(true);
  };

  const handleDeleteOil = () => {
    if (!deletingOil) return;

    deleteOilMutation.mutate(deletingOil.id, {
      onSuccess: () => {
        toast({
          title: "Aceite eliminado",
          description: `${deletingOil.name} ha sido eliminado`,
        });
        setDeletingOil(null);
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleDeleteMaterial = () => {
    if (!deletingMaterial) return;

    deleteMaterialMutation.mutate(deletingMaterial.id, {
      onSuccess: () => {
        toast({
          title: "Material eliminado",
          description: `${deletingMaterial.name} ha sido eliminado`,
        });
        setDeletingMaterial(null);
      },
      onError: (error) => {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      },
    });
  };

  const handleCloseOilDialog = (open: boolean) => {
    setShowOilDialog(open);
    if (!open) {
      setEditingOil(null);
    }
  };

  const handleCloseMaterialDialog = (open: boolean) => {
    setShowMaterialDialog(open);
    if (!open) {
      setEditingMaterial(null);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Stock de Ingredientes"
        description="Gestiona los aceites y materiales para producción"
        actions={
          <Button onClick={() => setShowNewDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nuevo Ingrediente
          </Button>
        }
      />

      {totalLowStock > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="flex items-center gap-4 pt-6">
            <AlertTriangle className="h-8 w-8 text-yellow-600" />
            <div>
              <p className="font-medium text-yellow-800">
                {totalLowStock} ingrediente{totalLowStock > 1 ? "s" : ""} con
                stock bajo
              </p>
              <p className="text-sm text-yellow-700">
                Revisa los niveles de inventario y considera crear órdenes de
                compra
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tipos de Aceite
            </CardTitle>
            <Droplet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{oilTypes.length}</div>
            {lowStockOil.length > 0 && (
              <p className="text-xs text-yellow-600">
                {lowStockOil.length} con stock bajo
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Stock Total Aceite
            </CardTitle>
            <Droplet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatNumber(
                oilTypes.reduce((acc, o) => acc + oilTotalStock(o.id), 0)
              )}
            </div>
            <p className="text-xs text-muted-foreground">toneladas</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Materiales</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {rawMaterials.filter((m) => !m.exclude_from_inventory).length}
            </div>
            {activeLowStockMaterials.length > 0 && (
              <p className="text-xs text-yellow-600">
                {activeLowStockMaterials.length} con stock bajo
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Alertas Activas
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalLowStock}</div>
            <p className="text-xs text-muted-foreground">
              ingredientes bajo mínimo
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Search Input */}
      <div className="relative w-full sm:max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar por código o nombre..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-9 pl-9"
        />
      </div>

      {/* Search Results or Tabbed View */}
      {searchTerm.trim() ? (
        /* Combined Search Results */
        <Card>
          <CardContent className="p-0">
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-muted-foreground">
                {filteredOilTypes.length + filteredRawMaterials.length}{" "}
                resultado(s) para "{searchTerm}"
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchInput("");
                  setSearchTerm("");
                }}
              >
                Limpiar búsqueda
              </Button>
            </div>

            {filteredOilTypes.length === 0 &&
            filteredRawMaterials.length === 0 ? (
              <div className="py-8 text-center">
                <Search className="mx-auto h-12 w-12 text-muted-foreground/50" />
                <p className="mt-4 text-muted-foreground">
                  No se encontraron resultados para "{searchTerm}"
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Oils Results */}
                {filteredOilTypes.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead
                          sortKey="code"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Código
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="name"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Nombre
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p1"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          P1
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p2"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          P2
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="total"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Total
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="nc"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          No Conforme
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="minimum"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Stock Mínimo
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="density"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Densidad
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cost"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Costo Unitario ($/kg)
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="status"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Estado
                        </SortableTableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedFilteredOilTypes.map((oil, index) => {
                        const isLow = lowStockOilIds.has(oil.id);
                        const isNegative =
                          getStockAt(oilStockByPlant, oil.id, "P1") < 0 ||
                          getStockAt(oilStockByPlant, oil.id, "P2") < 0;
                        return (
                          <TableRow
                            key={oil.id}
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell className="font-mono">
                              {oil.code}
                            </TableCell>
                            <TableCell className="font-medium">
                              {oil.name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(oilStockByPlant, oil.id, "P1")
                              )}{" "}
                              ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(oilStockByPlant, oil.id, "P2")
                              )}{" "}
                              ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(oilTotalStock(oil.id))} ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono text-muted-foreground">
                              {oilNcStock(oil.id) !== 0
                                ? `${formatNumber(oilNcStock(oil.id))} ton`
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {oil.minimum_stock_liters
                                ? `${formatNumber(oil.minimum_stock_liters)} ton`
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatNumber(oil.density_kg_per_liter, 4)} kg/L
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {oil.average_cost_clp
                                ? `$ ${formatNumber(
                                    costPerKgClp(
                                      oil.average_cost_clp,
                                      oil.density_kg_per_liter
                                    ),
                                    0
                                  )}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <StockStatusBadge
                                isLow={isLow}
                                isNegative={isNegative}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <IngredientActionsMenu
                                onViewMovements={() =>
                                  setMovementsIngredient({
                                    type: "oil",
                                    ingredient: oil,
                                  })
                                }
                                onAdjustStock={() =>
                                  setAdjustmentIngredient({
                                    type: "oil",
                                    ingredient: oil,
                                  })
                                }
                                onEdit={() => handleEditOil(oil)}
                                onDelete={() => setDeletingOil(oil)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {/* Materials Results */}
                {filteredRawMaterials.length > 0 && (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead
                          sortKey="code"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Código
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="name"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Nombre
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="unit"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Unidad
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p1"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          P1
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p2"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          P2
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="total"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Total
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="nc"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          No Conforme
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="minimum"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Stock Mínimo
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cost"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Costo Unitario
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="status"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Estado
                        </SortableTableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedFilteredRawMaterials.map((material, index) => {
                        const isLow = lowStockMaterialIds.has(material.id);
                        const isNegative =
                          getStockAt(
                            rawMaterialStockByPlant,
                            material.id,
                            "P1"
                          ) < 0 ||
                          getStockAt(
                            rawMaterialStockByPlant,
                            material.id,
                            "P2"
                          ) < 0;
                        return (
                          <TableRow
                            key={material.id}
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell className="font-mono">
                              {material.code}
                            </TableCell>
                            <TableCell className="font-medium">
                              {material.name}
                            </TableCell>
                            <TableCell>{material.unit}</TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(
                                  rawMaterialStockByPlant,
                                  material.id,
                                  "P1"
                                ),
                                0
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(
                                  rawMaterialStockByPlant,
                                  material.id,
                                  "P2"
                                ),
                                0
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(materialTotalStock(material.id), 0)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono text-muted-foreground">
                              {materialNcStock(material.id) !== 0
                                ? formatNumber(materialNcStock(material.id), 0)
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {material.minimum_stock_level
                                ? formatNumber(material.minimum_stock_level, 0)
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {material.average_cost_clp
                                ? `$ ${formatNumber(material.average_cost_clp, 0)}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <StockStatusBadge
                                isLow={isLow}
                                isNegative={isNegative}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <IngredientActionsMenu
                                onViewMovements={() =>
                                  setMovementsIngredient({
                                    type: "raw_material",
                                    ingredient: material,
                                  })
                                }
                                onAdjustStock={() =>
                                  setAdjustmentIngredient({
                                    type: "raw_material",
                                    ingredient: material,
                                  })
                                }
                                onEdit={() => handleEditMaterial(material)}
                                onDelete={() => setDeletingMaterial(material)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        /* Tabbed View (no search) */
        <Card>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="px-4 pt-4">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="oil" className="flex items-center gap-2">
                    <Droplet className="h-4 w-4" />
                    Aceites
                    {lowStockOil.length > 0 && (
                      <Badge variant="destructive" className="ml-1">
                        {lowStockOil.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger
                    value="materials"
                    className="flex items-center gap-2"
                  >
                    <Package className="h-4 w-4" />
                    Materiales
                    {lowStockMaterials.length > 0 && (
                      <Badge variant="destructive" className="ml-1">
                        {lowStockMaterials.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="oil" className="mt-0">
                {isLoadingOil ? (
                  <div className="space-y-2 p-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : oilTypes.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="mb-4 text-muted-foreground">
                      No hay tipos de aceite registrados
                    </p>
                    <Button onClick={() => setShowNewDialog(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Crear primer aceite
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead
                          sortKey="code"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Código
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="name"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Nombre
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p1"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          P1
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p2"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          P2
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="total"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Total
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="nc"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          No Conforme
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="minimum"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Stock Mínimo
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="density"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Densidad
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cost"
                          sort={oilSort}
                          onSort={handleOilSort}
                          className="text-right"
                        >
                          Costo Unitario ($/kg)
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="status"
                          sort={oilSort}
                          onSort={handleOilSort}
                        >
                          Estado
                        </SortableTableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedOilTypes.map((oil, index) => {
                        const isLow = lowStockOilIds.has(oil.id);
                        const isNegative =
                          getStockAt(oilStockByPlant, oil.id, "P1") < 0 ||
                          getStockAt(oilStockByPlant, oil.id, "P2") < 0;
                        return (
                          <TableRow
                            key={oil.id}
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell className="font-mono">
                              {oil.code}
                            </TableCell>
                            <TableCell className="font-medium">
                              {oil.name}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(oilStockByPlant, oil.id, "P1")
                              )}{" "}
                              ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(oilStockByPlant, oil.id, "P2")
                              )}{" "}
                              ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(oilTotalStock(oil.id))} ton
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono text-muted-foreground">
                              {oilNcStock(oil.id) !== 0
                                ? `${formatNumber(oilNcStock(oil.id))} ton`
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {oil.minimum_stock_liters
                                ? `${formatNumber(oil.minimum_stock_liters)} ton`
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap">
                              {formatNumber(oil.density_kg_per_liter, 4)} kg/L
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {oil.average_cost_clp
                                ? `$ ${formatNumber(
                                    costPerKgClp(
                                      oil.average_cost_clp,
                                      oil.density_kg_per_liter
                                    ),
                                    0
                                  )}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <StockStatusBadge
                                isLow={isLow}
                                isNegative={isNegative}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <IngredientActionsMenu
                                onViewMovements={() =>
                                  setMovementsIngredient({
                                    type: "oil",
                                    ingredient: oil,
                                  })
                                }
                                onAdjustStock={() =>
                                  setAdjustmentIngredient({
                                    type: "oil",
                                    ingredient: oil,
                                  })
                                }
                                onEdit={() => handleEditOil(oil)}
                                onDelete={() => setDeletingOil(oil)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>

              <TabsContent value="materials" className="mt-0">
                {isLoadingMaterials ? (
                  <div className="space-y-2 p-4">
                    {[...Array(5)].map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full" />
                    ))}
                  </div>
                ) : rawMaterials.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="mb-4 text-muted-foreground">
                      No hay materiales registrados
                    </p>
                    <Button onClick={() => setShowNewDialog(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Crear primer material
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableTableHead
                          sortKey="code"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Código
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="name"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Nombre
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="unit"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Unidad
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p1"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          P1
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="p2"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          P2
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="total"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Total
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="nc"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          No Conforme
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="minimum"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Stock Mínimo
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cost"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                          className="text-right"
                        >
                          Costo Unitario
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="status"
                          sort={materialSort}
                          onSort={handleMaterialSort}
                        >
                          Estado
                        </SortableTableHead>
                        <TableHead className="whitespace-nowrap text-right">
                          Acciones
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRawMaterials.map((material, index) => {
                        const isLow = lowStockMaterialIds.has(material.id);
                        const isNegative =
                          getStockAt(
                            rawMaterialStockByPlant,
                            material.id,
                            "P1"
                          ) < 0 ||
                          getStockAt(
                            rawMaterialStockByPlant,
                            material.id,
                            "P2"
                          ) < 0;
                        return (
                          <TableRow
                            key={material.id}
                            className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                            style={{
                              animationDelay: `${Math.min(index * 40, 400)}ms`,
                            }}
                          >
                            <TableCell className="font-mono">
                              {material.code}
                            </TableCell>
                            <TableCell className="font-medium">
                              {material.name}
                            </TableCell>
                            <TableCell>{material.unit}</TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(
                                  rawMaterialStockByPlant,
                                  material.id,
                                  "P1"
                                ),
                                0
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(
                                getStockAt(
                                  rawMaterialStockByPlant,
                                  material.id,
                                  "P2"
                                ),
                                0
                              )}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {formatNumber(materialTotalStock(material.id), 0)}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono text-muted-foreground">
                              {materialNcStock(material.id) !== 0
                                ? formatNumber(materialNcStock(material.id), 0)
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {material.minimum_stock_level
                                ? formatNumber(material.minimum_stock_level, 0)
                                : "-"}
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-right font-mono">
                              {material.average_cost_clp
                                ? `$ ${formatNumber(material.average_cost_clp, 0)}`
                                : "-"}
                            </TableCell>
                            <TableCell>
                              <StockStatusBadge
                                isLow={isLow}
                                isNegative={isNegative}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <IngredientActionsMenu
                                onViewMovements={() =>
                                  setMovementsIngredient({
                                    type: "raw_material",
                                    ingredient: material,
                                  })
                                }
                                onAdjustStock={() =>
                                  setAdjustmentIngredient({
                                    type: "raw_material",
                                    ingredient: material,
                                  })
                                }
                                onEdit={() => handleEditMaterial(material)}
                                onDelete={() => setDeletingMaterial(material)}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* New Ingredient Dialog */}
      <NewIngredientDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        defaultTab={activeTab === "materials" ? "material" : "oil"}
      />

      {/* Oil Type Dialog (for editing) */}
      <OilTypeDialog
        open={showOilDialog}
        onOpenChange={handleCloseOilDialog}
        oilType={editingOil}
      />

      {/* Raw Material Dialog */}
      <RawMaterialDialog
        open={showMaterialDialog}
        onOpenChange={handleCloseMaterialDialog}
        material={editingMaterial}
      />

      {/* Delete Oil Confirmation */}
      <AlertDialog
        open={!!deletingOil}
        onOpenChange={(open) => !open && setDeletingOil(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Tipo de Aceite</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar "{deletingOil?.name}"? Esta acción no se
              puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteOil}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Material Confirmation */}
      <AlertDialog
        open={!!deletingMaterial}
        onOpenChange={(open) => !open && setDeletingMaterial(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar Material</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar "{deletingMaterial?.name}"? Esta acción
              no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMaterial}
              className="bg-red-600 hover:bg-red-700"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stock Movements Dialog */}
      {movementsIngredient && (
        <StockMovementsDialog
          open={!!movementsIngredient}
          onOpenChange={(open) => !open && setMovementsIngredient(null)}
          ingredientType={movementsIngredient.type}
          ingredient={movementsIngredient.ingredient}
        />
      )}

      {/* Stock Adjustment Dialog */}
      {adjustmentIngredient && (
        <StockAdjustmentDialog
          open={!!adjustmentIngredient}
          onOpenChange={(open) => !open && setAdjustmentIngredient(null)}
          ingredientType={adjustmentIngredient.type}
          ingredient={adjustmentIngredient.ingredient}
        />
      )}
    </PageContainer>
  );
}

interface IngredientActionsMenuProps {
  onViewMovements: () => void;
  onAdjustStock: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

function IngredientActionsMenu({
  onViewMovements,
  onAdjustStock,
  onEdit,
  onDelete,
}: IngredientActionsMenuProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Acciones">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={onViewMovements}>
          <History className="mr-2 h-4 w-4" />
          Ver movimientos
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onAdjustStock}>
          <SlidersHorizontal className="mr-2 h-4 w-4" />
          Ajustar stock
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onEdit}>
          <Pencil className="mr-2 h-4 w-4" />
          Editar
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete} className="text-red-600">
          <Trash2 className="mr-2 h-4 w-4" />
          Eliminar
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface StockStatusBadgeProps {
  isLow: boolean;
  isNegative: boolean;
}

function StockStatusBadge({ isLow, isNegative }: StockStatusBadgeProps) {
  if (isNegative) {
    return (
      <Badge
        variant="outline"
        className="border-red-500 bg-red-50 text-red-700"
      >
        Stock Negativo
      </Badge>
    );
  }
  if (isLow) {
    return (
      <Badge
        variant="outline"
        className="border-yellow-500 bg-yellow-50 text-yellow-700"
      >
        Stock Bajo
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="border-green-500 bg-green-50 text-green-700"
    >
      OK
    </Badge>
  );
}
